// src/App.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

// Components
import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import MapView from './components/map/MapView';
import TaskSummary from './components/task-summary/TaskSummary';
import TripDrawer from './components/table/TripDrawer';
import Login from './components/auth/Login';
import UserInfoView from './components/user-info/UserInfoView';
import UserManagementConsole from './components/user-management/UserManagementConsole';
import ControlTower from './components/control-tower/ControlTower';
import { TimelineWorkspace } from './components/timeline/TripTimeline';
import { DEFAULT_SIDEBAR_PROPS } from './components/common/Sidebar';
import BlockingLoadingOverlay from './components/common/BlockingLoadingOverlay';
import RouteConfigModal from './components/config/RouteConfigModal';

// Services and types
import { tripPlanningService } from './api/services';
import {
  AppState,
  SortOption,
  TouchPoint,
  TouchPointHoverPreview,
  Trip,
  TripHoverPreview,
  ViewportOcclusionRect,
} from './models/types';
import { routingJobsApi, RoutingJobResponse, TripEditRequest } from './api/routingJobsApi';
import { getTripWindowMetrics } from './utils/formatters';
import {
  buildRouteGeometryByTrip,
  parseRouteGeometryCsv,
  RouteGeometryArcMap,
} from './routeGeometry/routeGeometry';

// Contexts
import { useAuth } from './context/auth';
import { useHub } from './context/hub';
import { useTask } from './context/task';
import {
  buildTimelineBounds,
  buildTimelineStrips,
  canBuildTimelineStripData,
  hasTimelineSupport,
} from './components/timeline/timelineUtils';

// Styles
import 'mapbox-gl/dist/mapbox-gl.css';

const TRIP_DRAWER_WIDTH = 408;
const TIMELINE_DRAWER_GAP = 16;
const TIMELINE_COLLAPSED_DRAWER_LEFT = 64;
const TIMELINE_TOP_OFFSET = 16;
const TIMELINE_EXPANDED_DRAWER_LEFT = TRIP_DRAWER_WIDTH + TIMELINE_DRAWER_GAP;

const getTimelineDefaultPosition = (drawerOpen = true) => ({
  x: drawerOpen ? TIMELINE_EXPANDED_DRAWER_LEFT : TIMELINE_COLLAPSED_DRAWER_LEFT,
  y: TIMELINE_TOP_OFFSET,
});

const areOcclusionRectsEqual = (left: ViewportOcclusionRect | null, right: ViewportOcclusionRect | null): boolean => {
  if (!left || !right) {
    return left === right;
  }
  return (
    Math.abs(left.left - right.left) < 1 &&
    Math.abs(left.top - right.top) < 1 &&
    Math.abs(left.right - right.right) < 1 &&
    Math.abs(left.bottom - right.bottom) < 1
  );
};

const areOcclusionRectArraysEqual = (left: ViewportOcclusionRect[], right: ViewportOcclusionRect[]): boolean => (
  left.length === right.length && left.every((rect, index) => areOcclusionRectsEqual(rect, right[index]))
);

const getTripTouchPointCount = (trip: Trip): number => {
  if (Array.isArray(trip.tpDetails) && trip.tpDetails.length > 0) {
    return trip.tpDetails.length;
  }
  const fallback = (trip as any).stopCount;
  return Number.isFinite(fallback) ? Number(fallback) : 0;
};

const matchesTripSearch = (trip: Trip, searchTerm: string): boolean => {
  const searchLower = searchTerm.toLowerCase().trim();
  if (!searchLower) {
    return true;
  }

  const matchesTripId =
    trip.name.toLowerCase().includes(searchLower) ||
    String(trip.vehTripNo ?? '').includes(searchLower) ||
    (trip.tripMeta?.loadId ?? '').toLowerCase().includes(searchLower);

  if (matchesTripId) {
    return true;
  }

  return trip.tpDetails.some((touchPoint) => {
    const fields = [
      touchPoint.name,
      touchPoint.orderNumber,
      touchPoint.customerName,
      touchPoint.generatorName,
      touchPoint.tpId,
    ];
    return fields.some((field) => field && field.toLowerCase().includes(searchLower));
  });
};

const resolveTripSortMetric = (trip: Trip, key: SortOption): string | number => {
  const fromPayload = trip.sortValues?.[key];
  if (typeof fromPayload === 'number') {
    return fromPayload;
  }
  if (typeof fromPayload === 'string') {
    const parsed = Number(fromPayload);
    return Number.isFinite(parsed) ? parsed : fromPayload.toLowerCase();
  }

  switch (key) {
    case 'weight':
      return trip.totalWtKg;
    case 'tpCount':
      return getTripTouchPointCount(trip);
    case 'distance':
      return trip.distKm;
    case 'duration':
      return trip.sortValues?.travelMinutes ?? getTripWindowMetrics(trip).durationMinutes ?? 0;
    case 'tripId':
    default:
      return trip.sortValues?.tripId ?? Number(trip.vehTripNo ?? 0);
  }
};

const getOrderedTimelineTripNames = (
  trips: Trip[],
  candidateTripNames: string[],
  searchTerm: string,
  sortConfig: { key: SortOption; direction: 'asc' | 'desc' }
): string[] => {
  const tripsByName = new Map(trips.map((trip) => [trip.name, trip]));

  return candidateTripNames
    .map((tripName, index) => ({ tripName, index, trip: tripsByName.get(tripName) }))
    .filter((entry): entry is { tripName: string; index: number; trip: Trip } =>
      Boolean(entry.trip) && matchesTripSearch(entry.trip as Trip, searchTerm)
    )
    .sort((left, right) => {
      const leftMetric = resolveTripSortMetric(left.trip, sortConfig.key);
      const rightMetric = resolveTripSortMetric(right.trip, sortConfig.key);

      if (leftMetric < rightMetric) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (leftMetric > rightMetric) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return left.index - right.index;
    })
    .map((entry) => entry.tripName);
};

// Define the AppProps interface and export it
export interface AppProps {
  initialView?: 'taskSummary' | 'taskDetail' | 'userInfo' | 'userManagement' | 'controlTower';
}

/**
 * Main application component
 * Handles routing between task summary and detail views
 */
const App: React.FC<AppProps> = ({ initialView = 'taskSummary' }) => {
  // ----------------
  // Hooks
  // ----------------
  // Router hooks
  const { hubId, taskId } = useParams<{ hubId: string; taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Context hooks
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { currentHubId, changeHub, hubs, fetchHubs } = useHub();
  const { fetchTasks } = useTask();
  
  // ----------------
  // State
  // ----------------
  // View state
  const [currentView, setCurrentView] = useState<'taskSummary' | 'taskDetail' | 'userInfo' | 'userManagement' | 'controlTower'>(
    taskId ? 'taskDetail' : initialView
  );
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(taskId || null);
  const [selectedDepotId, setSelectedDepotId] = useState<string | null>(hubId || currentHubId || null);
  const [viewOutputMode, setViewOutputMode] = useState<'edited' | 'original'>('edited');
  const [routeConfigOpen, setRouteConfigOpen] = useState(false);
  const [routeGeometryArcs, setRouteGeometryArcs] = useState<RouteGeometryArcMap | null>(null);
  const [routeGeometrySupported, setRouteGeometrySupported] = useState(false);
  const [routeGeometryRefreshing, setRouteGeometryRefreshing] = useState(false);
  const [loadedRoutingJob, setLoadedRoutingJob] = useState<RoutingJobResponse | null>(null);
  const [createTripsOpen, setCreateTripsOpen] = useState(false);
  const [tripDispatchDate, setTripDispatchDate] = useState('');
  const [creatingTrips, setCreatingTrips] = useState(false);
  
  // Task detail view state
  const [state, setState] = useState<AppState>({
    taskInfo: null,
    loading: true,
    error: null,
    selectedTrips: [],
    visibleTrips: [],
    showPolygons: false,
    showSequence: false,
    editMode: false,
    editSourceTrip: null,
    selectedTouchPoints: [],
    searchTerm: '',
    sortConfig: {
      key: 'tripId',
      direction: 'asc'
    },
    orders: [],
    hoveredTouchPoint: null,
  });
  const [focusLngLat, setFocusLngLat] = useState<[number, number] | null>(null);
  const [focusTouchPointRequest, setFocusTouchPointRequest] = useState<{
    lngLat: [number, number];
    requestId: number;
  } | null>(null);
  const [focusTripRequest, setFocusTripRequest] = useState<{ tripName: string; requestId: number } | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [geoCorrectionTouchPoint, setGeoCorrectionTouchPoint] = useState<TouchPoint | null>(null);
  const [geoCorrectionDraftLngLat, setGeoCorrectionDraftLngLat] = useState<[number, number] | null>(null);
  const [geoCorrectionSubmitting, setGeoCorrectionSubmitting] = useState(false);
  const [activeTouchPointKey, setActiveTouchPointKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [tableTimelinesEnabled, setTableTimelinesEnabled] = useState(false);
  const [tableTimelineTripOrder, setTableTimelineTripOrder] = useState<string[]>([]);
  const [tableTimelineHiddenTrips, setTableTimelineHiddenTrips] = useState<string[]>([]);
  const [tableTimelineOrderPinned, setTableTimelineOrderPinned] = useState(false);
  const [openTimelineTrips, setOpenTimelineTrips] = useState<string[]>([]);
  const [tripDrawerOpen, setTripDrawerOpen] = useState(true);
  const [timelineOverlayPosition, setTimelineOverlayPosition] = useState(() => getTimelineDefaultPosition(true));
  const [timelineOverlayWidth, setTimelineOverlayWidth] = useState<number | null>(null);
  const [timelineOverlayHeight, setTimelineOverlayHeight] = useState<number | null>(null);
  const [externalTouchPointHover, setExternalTouchPointHover] = useState<TouchPointHoverPreview | null>(null);
  const [externalTripHover, setExternalTripHover] = useState<TripHoverPreview | null>(null);
  const [tripDrawerOcclusionRects, setTripDrawerOcclusionRects] = useState<ViewportOcclusionRect[]>([]);
  const [timelineOcclusionRect, setTimelineOcclusionRect] = useState<ViewportOcclusionRect | null>(null);
  const tripFocusRequestCounterRef = useRef(0);
  const touchPointFocusRequestCounterRef = useRef(0);
  const previousTripDrawerOpenRef = useRef(tripDrawerOpen);
  const defaultTimelinePosition = useCallback(() => getTimelineDefaultPosition(true), []);
  const currentHub = useMemo(
    () => hubs.find((hub) => hub.hubId === (currentHubId || hubId)),
    [hubs, currentHubId, hubId]
  );

  const taskStatus = (loadedRoutingJob?.status || state.taskInfo?.status)?.toUpperCase();
  const isJumboBuyTask = (loadedRoutingJob?.depotSnapshot?.tenantId || state.taskInfo?.tenant)?.toLowerCase() === 'jumbobuy';
  const canCreateTrips = isJumboBuyTask && (taskStatus === 'COMPLETED' || taskStatus === 'EDIT');
  const tripsAlreadyCreated = taskStatus === 'TRIPS_CREATED';

  // ----------------
  // Effects
  // ----------------
  // Ensure hubs are loaded when user is authenticated
  // Set the correct view based on initialView prop
  useEffect(() => {
    setCurrentView(initialView);
    
    if (initialView === 'taskDetail' && taskId) {
      setSelectedTaskId(taskId);
      setSelectedDepotId((prev) => prev || hubId || null);
      setViewOutputMode('edited');
    }
  }, [initialView, taskId, hubId]);

  // Update hub selection from URL
  useEffect(() => {
    console.log('[App] URL hubId changed:', { hubId, currentHubId });
    if (hubId && hubId !== 'default' && hubId !== currentHubId) {
      console.log('[App] Changing hub based on URL to:', hubId);
      
      // Add a small delay to prevent race conditions with other hub changes
      const timer = setTimeout(() => {
        changeHub(hubId);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [hubId, currentHubId, changeHub]);

  // Update URL when view or hubId changes
  useEffect(() => {
    if (!isAuthenticated) return;
    
    if (currentView === 'taskSummary') {
      const targetHubId = currentHubId || hubId || 'default';
      // Only navigate if we're not already on the right path
      if (!location.pathname.includes(`/task-summary/hub/${targetHubId}`)) {
        console.log('[App] Navigating to task summary view with hub:', targetHubId);
        navigate(`/task-summary/hub/${targetHubId}`);
      }
    } else if (currentView === 'taskDetail' && selectedTaskId) {
      const targetHubId = selectedDepotId || hubId || currentHubId || 'default';
      // Only navigate if we're not already on the right path
      if (!location.pathname.includes(`/edit-task/hub/${targetHubId}/task/${selectedTaskId}`)) {
        console.log('[App] Navigating to task detail view with hub:', targetHubId);
        navigate(`/edit-task/hub/${targetHubId}/task/${selectedTaskId}`);
      }
    } else if (currentView === 'userInfo') {
      const targetHubId = currentHubId || hubId || 'default';
      // Only navigate if we're not already on the right path
      if (!location.pathname.includes(`/user-info/hub/${targetHubId}`)) {
        console.log('[App] Navigating to user info view with hub:', targetHubId);
        navigate(`/user-info/hub/${targetHubId}`);
      }
    } else if (currentView === 'userManagement') {
      const targetHubId = currentHubId || hubId || 'default';
      if (!location.pathname.includes(`/user-management/hub/${targetHubId}`)) {
        console.log('[App] Navigating to user management with hub:', targetHubId);
        navigate(`/user-management/hub/${targetHubId}`);
      }
    } else if (currentView === 'controlTower') {
      if (location.pathname !== '/control-tower') {
        console.log('[App] Navigating to control tower');
        navigate('/control-tower');
      }
    }
  }, [currentView, currentHubId, selectedDepotId, selectedTaskId, hubId, isAuthenticated, navigate, location.pathname]);

  // Effect to fetch tasks when hub changes
  useEffect(() => {
    if (isAuthenticated && currentHubId) {
      if (currentView === 'taskSummary') {
        console.log('[App] Fetching tasks for hub:', currentHubId);
        fetchTasks(currentHubId);
      }
    }
  }, [isAuthenticated, currentHubId, currentView, fetchTasks]);

  const fetchTaskDetails = useCallback(async (
    taskId: string,
    depotId: string,
    outputMode: 'edited' | 'original'
  ) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      setExternalTouchPointHover(null);
      setExternalTripHover(null);
      setGeoCorrectionTouchPoint(null);
      setGeoCorrectionDraftLngLat(null);
      setGeoCorrectionSubmitting(false);
      setFocusLngLat(null);
      setFocusTouchPointRequest(null);
      setFocusTripRequest(null);
      setActiveTouchPointKey(null);
      const [{ taskInfo, orders }, routingJob] = await Promise.all([
        tripPlanningService.getTaskInfo(depotId, taskId, outputMode),
        routingJobsApi.getJob(depotId, taskId),
      ]);
      setLoadedRoutingJob(routingJob);
      // Initialize selected and visible trips from the task info
      const tripNames = taskInfo.trips.map((trip: any) => trip.name);
      const enableTimelinesByDefault = hasTimelineSupport(taskInfo);
      setOpenTimelineTrips([]);
      setTableTimelineTripOrder([]);
      setTableTimelineHiddenTrips([]);
      setTableTimelineOrderPinned(false);
      setTableTimelinesEnabled(enableTimelinesByDefault);
      setTimelineOverlayPosition(defaultTimelinePosition());
      setTimelineOverlayWidth(null);
      setTimelineOverlayHeight(null);
      setState(prev => ({
        ...prev,
        taskInfo,
        orders: orders ?? [],
        loading: false,
        selectedTrips: tripNames,
        visibleTrips: tripNames,
        showPolygons: taskInfo.tenant?.toLowerCase() === 'jumbobuy',
        showSequence: false,
        hoveredTouchPoint: null,
      }));
    } catch (error) {
      console.error('Failed to fetch task details:', error);
      setLoadedRoutingJob(null);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load task details. Please try again.',
      }));
    }
  }, [defaultTimelinePosition]);

  // Effect to fetch task details when selecting a specific task
  const resolvedTaskDepotId = selectedDepotId || hubId || currentHubId;
  useEffect(() => {
    if (currentView === 'taskDetail' && selectedTaskId && resolvedTaskDepotId) {
      fetchTaskDetails(selectedTaskId, resolvedTaskDepotId, viewOutputMode);
    }
  }, [currentView, selectedTaskId, resolvedTaskDepotId, fetchTaskDetails, viewOutputMode]);

  useEffect(() => {
    const loadedTaskId = state.taskInfo?.taskId;
    if (currentView !== 'taskDetail' || !loadedTaskId || !resolvedTaskDepotId) {
      setRouteGeometryArcs(null);
      setRouteGeometrySupported(false);
      return;
    }

    let cancelled = false;
    setRouteGeometryArcs(null);
    setRouteGeometrySupported(false);
    routingJobsApi.getRouteGeometry(resolvedTaskDepotId, loadedTaskId)
      .then((csv) => {
        if (!cancelled) {
          setRouteGeometryArcs(parseRouteGeometryCsv(csv));
          setRouteGeometrySupported(true);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setRouteGeometryArcs(null);
          setRouteGeometrySupported(false);
          if (error?.response?.status !== 404) {
            console.warn('Unable to load route geometry artifact', error);
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentView, resolvedTaskDepotId, state.taskInfo?.taskId]);

  const routeGeometryByTrip = useMemo(() => {
    if (!state.taskInfo || !routeGeometryArcs) {
      return undefined;
    }
    return buildRouteGeometryByTrip(state.taskInfo, routeGeometryArcs);
  }, [routeGeometryArcs, state.taskInfo]);

  const refreshRouteGeometry = useCallback(async () => {
    if (!selectedTaskId || !resolvedTaskDepotId || routeGeometryRefreshing) {
      return;
    }
    try {
      setRouteGeometryRefreshing(true);
      const result = await routingJobsApi.refreshRouteGeometry(
        resolvedTaskDepotId,
        selectedTaskId,
        viewOutputMode
      );
      const csv = await routingJobsApi.getRouteGeometry(resolvedTaskDepotId, selectedTaskId);
      setRouteGeometryArcs(parseRouteGeometryCsv(csv));
      setRouteGeometrySupported(true);
      setFeedback({ open: true, message: result.message, severity: 'success' });
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to refresh routes.';
      setFeedback({ open: true, message, severity: 'error' });
    } finally {
      setRouteGeometryRefreshing(false);
    }
  }, [resolvedTaskDepotId, routeGeometryRefreshing, selectedTaskId, viewOutputMode]);

  const openCreateTrips = () => {
    const timezone = loadedRoutingJob?.depotSnapshot?.configFilesMap?.timezone || 'Asia/Kolkata';
    let defaultDate = new Date().toISOString().slice(0, 10);
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date());
      const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
      defaultDate = `${values.year}-${values.month}-${values.day}`;
    } catch {
      // The service applies the authoritative timezone validation and IST fallback.
    }
    setTripDispatchDate(defaultDate);
    setCreateTripsOpen(true);
  };

  const submitCreateTrips = async () => {
    if (!resolvedTaskDepotId || !selectedTaskId || !tripDispatchDate || creatingTrips) return;
    try {
      setCreatingTrips(true);
      const result = await routingJobsApi.createTrips(resolvedTaskDepotId, selectedTaskId, tripDispatchDate);
      setCreateTripsOpen(false);
      setFeedback({ open: true, severity: 'success', message: `${result.tripCount} trips created.` });
      navigate(`/trips/hub/${resolvedTaskDepotId}?date=${result.dispatchDate}`);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to create trips.';
      setFeedback({ open: true, severity: 'error', message });
    } finally {
      setCreatingTrips(false);
    }
  };

  // ----------------
  // Event Handlers
  // ----------------
  // View navigation handlers
  const handleEditTask = (taskId: string, depotId: string) => {
    setSelectedTaskId(taskId);
    setSelectedDepotId(depotId);
    setViewOutputMode('edited');
    setCurrentView('taskDetail');
    navigate(`/edit-task/hub/${depotId || currentHubId || 'default'}/task/${taskId}`);
  };

  // Placeholder for future implementation
  const handleVerifyTask = (taskId: string, depotId: string) => {
    setSelectedTaskId(taskId);
    setSelectedDepotId(depotId);
    navigate(`/verify-task/hub/${depotId || currentHubId || 'default'}/task/${taskId}`);
  };

  const handleBackToSummary = () => {
    setCurrentView('taskSummary');
    setSelectedTaskId(null);
    navigate(`/task-summary/hub/${currentHubId || 'default'}`);
  };

  // Trip management handlers
  const handleTripSelection = (tripName: string) => {
    setState(prev => {
      const selected = [...prev.selectedTrips];
      const index = selected.indexOf(tripName);
      
      if (index === -1) {
        selected.push(tripName);
      } else {
        selected.splice(index, 1);
      }
      
      return { ...prev, selectedTrips: selected };
    });
  };

  const handleTripVisibility = (tripName: string) => {
    setState(prev => {
      const visible = [...prev.visibleTrips];
      const index = visible.indexOf(tripName);
      
      if (index === -1) {
        visible.push(tripName);
      } else {
        visible.splice(index, 1);
      }
      
      return { ...prev, visibleTrips: visible };
    });
  };

  // Search and filtering handlers
  const handleSearch = (term: string) => {
    setTableTimelineOrderPinned(false);
    setState(prev => ({ ...prev, searchTerm: term }));
  };

  const handleTripSortKeyChange = (key: SortOption) => {
    setTableTimelineOrderPinned(false);
    setState(prev => ({
      ...prev,
      sortConfig: {
        ...prev.sortConfig,
        key,
      },
    }));
  };

  const handleTripSortDirectionChange = (direction: 'asc' | 'desc') => {
    setTableTimelineOrderPinned(false);
    setState(prev => ({
      ...prev,
      sortConfig: {
        ...prev.sortConfig,
        direction,
      },
    }));
  };
  const touchPointKey = (touchPoint: TouchPoint | null | undefined) =>
    touchPoint ? `${touchPoint.tpId}::${touchPoint.seqNo}` : null;

  const clearExternalTouchPointHover = useCallback(() => {
    setExternalTouchPointHover(null);
  }, []);

  const handleTripHover = useCallback((preview: TouchPointHoverPreview | null) => {
    setExternalTouchPointHover(preview);
  }, []);

  const handleTimelineTripHover = useCallback((preview: TripHoverPreview | null) => {
    setExternalTripHover(preview);
  }, []);

  const requestMapPointFocus = useCallback((lngLat: [number, number]) => {
    setFocusLngLat(lngLat);
    touchPointFocusRequestCounterRef.current += 1;
    setFocusTouchPointRequest({
      lngLat,
      requestId: touchPointFocusRequestCounterRef.current,
    });
  }, []);

  const handleTripMapFocus = useCallback((tripName: string) => {
    clearExternalTouchPointHover();
    setExternalTripHover(null);
    setActiveTouchPointKey(null);
    setFocusLngLat(null);
    setFocusTouchPointRequest(null);
    setState((prev) => {
      if (!prev.taskInfo?.trips.some((trip) => trip.name === tripName)) {
        return prev;
      }

      return {
        ...prev,
        visibleTrips: prev.visibleTrips.includes(tripName)
          ? prev.visibleTrips
          : [...prev.visibleTrips, tripName],
        hoveredTouchPoint: null,
      };
    });
    tripFocusRequestCounterRef.current += 1;
    setFocusTripRequest({
      tripName,
      requestId: tripFocusRequestCounterRef.current,
    });
  }, [clearExternalTouchPointHover]);

  const handleTripTouchPointFocus = (touchPoint: TouchPoint) => {
    clearExternalTouchPointHover();
    setExternalTripHover(null);
    requestMapPointFocus(touchPoint.lngLat);
    setActiveTouchPointKey(touchPointKey(touchPoint));
    setState((prev) => ({ ...prev, hoveredTouchPoint: touchPoint }));
  };

  const isOriginalMode = viewOutputMode === 'original';
  const taskReadOnly = isOriginalMode || tripsAlreadyCreated;
  const timelineSupported = useMemo(() => hasTimelineSupport(state.taskInfo), [state.taskInfo]);
  const focusOcclusionRects = useMemo(
    () => [
      ...tripDrawerOcclusionRects,
      ...(timelineOcclusionRect ? [timelineOcclusionRect] : []),
    ],
    [timelineOcclusionRect, tripDrawerOcclusionRects]
  );
  const handleTripDrawerOcclusionRectsChange = useCallback((rects: ViewportOcclusionRect[]) => {
    setTripDrawerOcclusionRects((prev) => (
      areOcclusionRectArraysEqual(prev, rects) ? prev : rects
    ));
  }, []);
  const handleTimelineOcclusionRectChange = useCallback((rect: ViewportOcclusionRect | null) => {
    setTimelineOcclusionRect((prev) => (
      areOcclusionRectsEqual(prev, rect) ? prev : rect
    ));
  }, []);

  useEffect(() => {
    if (!state.taskInfo) {
      setOpenTimelineTrips([]);
      setTableTimelineTripOrder([]);
      setTableTimelineHiddenTrips([]);
      setTableTimelineOrderPinned(false);
      return;
    }
    setOpenTimelineTrips((prev) =>
      prev.filter((tripName) => state.taskInfo?.trips.some((trip) => trip.name === tripName))
    );
    setTableTimelineHiddenTrips((prev) =>
      prev.filter((tripName) => state.taskInfo?.trips.some((trip) => trip.name === tripName))
    );
    setTableTimelineTripOrder((prev) =>
      prev.filter((tripName) => state.taskInfo?.trips.some((trip) => trip.name === tripName))
    );
  }, [state.taskInfo]);

  const activeTimelineTripNames = useMemo(() => {
    if (!timelineSupported || !state.taskInfo) {
      return [];
    }
    const hiddenTripNames = new Set(tableTimelinesEnabled ? tableTimelineHiddenTrips : []);
    if (tableTimelinesEnabled) {
      return tableTimelineTripOrder.filter((tripName) => !hiddenTripNames.has(tripName));
    }

    return getOrderedTimelineTripNames(
      state.taskInfo.trips,
      openTimelineTrips,
      state.searchTerm,
      state.sortConfig
    ).filter((tripName) => !hiddenTripNames.has(tripName));
  }, [
    openTimelineTrips,
    state.searchTerm,
    state.sortConfig,
    state.taskInfo,
    tableTimelineHiddenTrips,
    tableTimelineTripOrder,
    tableTimelinesEnabled,
    timelineSupported,
  ]);

  const openTimelineStripData = useMemo(() => {
    if (!timelineSupported || !state.taskInfo) {
      return [];
    }
    return buildTimelineStrips(state.taskInfo, activeTimelineTripNames);
  }, [activeTimelineTripNames, state.taskInfo, timelineSupported]);

  const overlayTimelineBounds = useMemo(
    () => buildTimelineBounds(openTimelineStripData),
    [openTimelineStripData]
  );

  const clampTimelinePosition = useCallback((position: { x: number; y: number }) => {
    return {
      x: Math.max(12, position.x),
      y: Math.max(12, position.y),
    };
  }, []);

  useEffect(() => {
    const previousTripDrawerOpen = previousTripDrawerOpenRef.current;
    previousTripDrawerOpenRef.current = tripDrawerOpen;
    if (previousTripDrawerOpen === tripDrawerOpen) {
      return;
    }
    if (!openTimelineStripData.length) {
      return;
    }

    const defaultX = tripDrawerOpen ? TIMELINE_EXPANDED_DRAWER_LEFT : TIMELINE_COLLAPSED_DRAWER_LEFT;
    const previousDefaultX = previousTripDrawerOpen ? TIMELINE_EXPANDED_DRAWER_LEFT : TIMELINE_COLLAPSED_DRAWER_LEFT;

    setTimelineOverlayPosition((prev) => {
      const nextX = Math.abs(prev.x - previousDefaultX) <= 12
        ? defaultX
        : prev.x;
      return clampTimelinePosition({ ...prev, x: nextX });
    });
  }, [clampTimelinePosition, openTimelineStripData.length, tripDrawerOpen]);

  const handleOpenTripTimeline = useCallback((tripName: string, anchor?: { x: number; y: number }) => {
    if (!state.taskInfo) {
      return;
    }
    const trip = state.taskInfo.trips.find((candidate) => candidate.name === tripName);
    if (!trip || !canBuildTimelineStripData(trip)) {
      return;
    }
    setOpenTimelineTrips((prev) => {
      if (prev.includes(tripName)) {
        return prev;
      }
      if (anchor && prev.length === 0) {
        setTimelineOverlayPosition(clampTimelinePosition(anchor));
      } else if (prev.length === 0) {
        setTimelineOverlayPosition(clampTimelinePosition(getTimelineDefaultPosition(tripDrawerOpen)));
      }
      return [...prev, tripName];
    });
  }, [clampTimelinePosition, state.taskInfo, tripDrawerOpen]);

  const handleCloseTripTimeline = useCallback((tripName: string) => {
    clearExternalTouchPointHover();
    setExternalTripHover(null);
    if (tableTimelinesEnabled) {
      setTableTimelineHiddenTrips((prev) => (
        prev.includes(tripName) ? prev : [...prev, tripName]
      ));
    } else {
      setOpenTimelineTrips((prev) => prev.filter((name) => name !== tripName));
    }
    setState((prev) => ({ ...prev, hoveredTouchPoint: null }));
  }, [clearExternalTouchPointHover, tableTimelinesEnabled]);

  const handleCloseAllTimelines = useCallback(() => {
    clearExternalTouchPointHover();
    setExternalTripHover(null);
    if (tableTimelinesEnabled) {
      setTableTimelinesEnabled(false);
      setTableTimelineHiddenTrips([]);
      setTableTimelineOrderPinned(false);
      setOpenTimelineTrips([]);
    } else {
      setOpenTimelineTrips([]);
    }
    setState((prev) => ({ ...prev, hoveredTouchPoint: null }));
  }, [clearExternalTouchPointHover, tableTimelinesEnabled]);

  const handleReorderTripTimeline = useCallback((tripName: string, targetIndex: number) => {
    const applyReorder = (prev: string[]) => {
      const currentIndex = prev.indexOf(tripName);
      if (currentIndex === -1 || currentIndex === targetIndex) {
        return prev;
      }
      const next = prev.slice();
      next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, tripName);
      return next;
    };

    if (tableTimelinesEnabled) {
      setTableTimelineOrderPinned(true);
      setTableTimelineTripOrder(applyReorder);
      return;
    }

    setOpenTimelineTrips(applyReorder);
  }, [tableTimelinesEnabled]);

  const handleTableTimelinesToggle = useCallback((enabled: boolean) => {
    if (!timelineSupported) {
      return;
    }
    setTableTimelinesEnabled(enabled);
    if (enabled) {
      setTableTimelineHiddenTrips([]);
      setTableTimelineOrderPinned(false);
      setTimelineOverlayPosition(clampTimelinePosition(getTimelineDefaultPosition(tripDrawerOpen)));
      setTimelineOverlayWidth(null);
      setTimelineOverlayHeight(null);
    }
  }, [clampTimelinePosition, timelineSupported, tripDrawerOpen]);

  const handleTableTimelineOrderChange = useCallback((tripNames: string[]) => {
    setTableTimelineTripOrder((prev) => {
      if (!tableTimelineOrderPinned) {
        return prev.length === tripNames.length && prev.every((tripName, index) => tripName === tripNames[index])
          ? prev
          : tripNames;
      }
      const existing = prev.filter((tripName) => tripNames.includes(tripName));
      const appended = tripNames.filter((tripName) => !existing.includes(tripName));
      const next = [...existing, ...appended];
      return prev.length === next.length && prev.every((tripName, index) => tripName === next[index])
        ? prev
        : next;
    });
  }, [tableTimelineOrderPinned]);

  const handleMapOptions = (option: 'polygons' | 'sequence') => {
    setState(prev => {
      if (option === 'polygons') {
        return { ...prev, showPolygons: !prev.showPolygons };
      } else {
        return { ...prev, showSequence: !prev.showSequence };
      }
    });
  };

  // Map-first edit handlers
  const startTripEdit = (tripName: string, initialTouchPoints: TouchPoint[] = []) => {
    if (taskReadOnly) {
      return;
    }
    clearExternalTouchPointHover();
    setGeoCorrectionTouchPoint(null);
    setGeoCorrectionDraftLngLat(null);
    setState(prev => {
      const sourceTrip = prev.taskInfo?.trips.find((trip) => trip.name === tripName);
      const validInitialTouchPoints = sourceTrip
        ? initialTouchPoints.filter((touchPoint) => sourceTrip.tpDetails.some((tp) => tp.tpId === touchPoint.tpId))
        : [];

      return {
        ...prev,
        editMode: true,
        editSourceTrip: tripName,
        selectedTouchPoints: validInitialTouchPoints,
        error: null,
      };
    });
  };

  const cancelTripEdit = () => {
    setState(prev => ({
      ...prev,
      editMode: false,
      editSourceTrip: null,
      selectedTouchPoints: [],
    }));
  };

  const startGeoCorrection = (touchPoint: TouchPoint) => {
    if (taskReadOnly || touchPoint.geoCorrectionSupported === false) {
      return;
    }
    clearExternalTouchPointHover();
    cancelTripEdit();
    setActiveTouchPointKey(touchPointKey(touchPoint));
    setState((prev) => ({ ...prev, hoveredTouchPoint: touchPoint, error: null }));
    setGeoCorrectionTouchPoint(touchPoint);
    setGeoCorrectionDraftLngLat(null);
  };

  const cancelGeoCorrection = () => {
    setGeoCorrectionTouchPoint(null);
    setGeoCorrectionDraftLngLat(null);
  };

  const handleOutputModeChange = (mode: 'edited' | 'original') => {
    if (mode === viewOutputMode) {
      return;
    }
    setViewOutputMode(mode);
    if (mode === 'original') {
      cancelTripEdit();
      cancelGeoCorrection();
    }
  };

  const handleGeoCorrectionDraft = (lngLat: [number, number]) => {
    setGeoCorrectionDraftLngLat(lngLat);
  };

  const toggleEditTouchPoint = (touchPoint: TouchPoint) => {
    setState(prev => {
      if (taskReadOnly || !prev.editMode || !prev.taskInfo || !prev.editSourceTrip) {
        return prev;
      }
      const sourceTrip = prev.taskInfo.trips.find((trip) => trip.name === prev.editSourceTrip);
      if (!sourceTrip || !sourceTrip.tpDetails.some((tp) => tp.tpId === touchPoint.tpId)) {
        return prev;
      }
      const touchPoints = [...prev.selectedTouchPoints];
      const index = touchPoints.findIndex(tp => tp.tpId === touchPoint.tpId);
      
      if (index === -1) {
        touchPoints.push(touchPoint);
      } else {
        touchPoints.splice(index, 1);
      }
      
      return { ...prev, selectedTouchPoints: touchPoints };
    });
  };

  const confirmGeoCorrection = async () => {
    if (taskReadOnly || !geoCorrectionTouchPoint || !geoCorrectionDraftLngLat || !selectedTaskId) {
      return;
    }
    const resolvedDepotId = selectedDepotId || hubId || currentHubId;
    if (!resolvedDepotId) {
      setState((prev) => ({
        ...prev,
        error: 'Unable to apply geo correction because depot is not selected.',
      }));
      return;
    }

    try {
      setGeoCorrectionSubmitting(true);
      const result = await tripPlanningService.applyGeoLocationCorrection({
        depotId: resolvedDepotId,
        taskId: selectedTaskId,
        request: {
          screen: 'edit',
          touchPointRef: geoCorrectionTouchPoint.geoCorrectionRef ?? geoCorrectionTouchPoint.tpId,
          locationType: geoCorrectionTouchPoint.geoCorrectionRole,
          latitude: geoCorrectionDraftLngLat[1],
          longitude: geoCorrectionDraftLngLat[0],
        },
      });
      setState((prev) => ({
        ...prev,
        taskInfo: result.taskInfo,
        error: null,
        hoveredTouchPoint: null,
      }));
      requestMapPointFocus(geoCorrectionDraftLngLat);
      setFeedback({
        open: true,
        message: result.message,
        severity: 'success',
      });
      cancelGeoCorrection();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to update geo location.';
      setState((prev) => ({
        ...prev,
        error: message,
      }));
      setFeedback({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setGeoCorrectionSubmitting(false);
    }
  };

  const applyTripEdit = async (request: TripEditRequest): Promise<boolean> => {
    if (taskReadOnly || !state.taskInfo || request.touchPointIds.length === 0 || !selectedTaskId) {
      return false;
    }
    const resolvedDepotId = selectedDepotId || hubId || currentHubId;
    if (!resolvedDepotId) {
      setState((prev) => ({
        ...prev,
        error: 'Unable to apply edit because depot is not selected.',
      }));
      return false;
    }

    try {
      setEditSubmitting(true);
      const updatedTaskInfo = await tripPlanningService.applyTripEdit({
        depotId: resolvedDepotId,
        taskId: selectedTaskId,
        request,
      });
      const tripNames = updatedTaskInfo.trips.map((trip) => trip.name);
      setViewOutputMode('edited');
      setState((prev) => ({
        ...prev,
        taskInfo: updatedTaskInfo,
        selectedTrips: tripNames,
        visibleTrips: tripNames,
        editMode: false,
        editSourceTrip: null,
        selectedTouchPoints: [],
        error: null,
      }));
      setFocusTripRequest(null);
      return true;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to update trips. Please try again.';
      console.error('Failed to move touch points:', error);
      setState(prev => ({
        ...prev,
        error: message,
      }));
      return false;
    } finally {
      setEditSubmitting(false);
    }
  };

  // Add handler for UserInfo click in the Sidebar
  const handleUserInfoClick = () => {
    // Just update the current view without full navigation
    console.log('[App] Switching to user info view');
    
    // Update view state
    setCurrentView('userInfo');
    
    // Use replace instead of push to avoid the page refresh feeling
    navigate(`/user-info/hub/${currentHubId || 'default'}`, { replace: true });
  };

  const handleUserManagementClick = () => {
    console.log('[App] Switching to user management view');
    setSelectedTaskId(null);
    setCurrentView('userManagement');
    navigate(`/user-management/hub/${currentHubId || 'default'}`, { replace: true });
  };

  const handleDepotConfigurationClick = () => {
    console.log('[App] Opening depot configuration modal');
    setSelectedTaskId(null);
    setRouteConfigOpen(true);
  };

  const handleControlTowerClick = () => {
    console.log('[App] Switching to control tower view');
    setSelectedTaskId(null);
    setCurrentView('controlTower');
    navigate('/control-tower', { replace: true });
  };
  
  // Add handler for Route Plan click in the Sidebar
  const handleRoutePlanClick = () => {
    // Just update the current view without full navigation if already authenticated
    if (isAuthenticated) {
      console.log('[App] Switching to task summary view');
      
      // Just set the view without navigating directly
      setCurrentView('taskSummary');
      setSelectedTaskId(null);
      
      // Use replace instead of push to avoid adding to history stack
      navigate(`/task-summary/hub/${currentHubId || 'default'}`, { replace: true });
    }
  };

  // ----------------
  // Render
  // ----------------
  // Show loading spinner during authentication
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // Main application content
  return (
    <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>
      {currentView === 'taskSummary' ? (
        // Task summary view
        <TaskSummary 
          onEditTask={handleEditTask}
          onVerifyTask={handleVerifyTask}
          onDepotConfigurationClick={handleDepotConfigurationClick}
        />
      ) : currentView === 'userInfo' ? (
        // User info view
        <UserInfoView onDepotConfigurationClick={handleDepotConfigurationClick} />
      ) : currentView === 'controlTower' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
          <Header
            taskInfo={null}
            loading={false}
            isTaskSummary
            titleOverride="Control Tower"
            showHubSelector={false}
          />
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <Sidebar
              {...DEFAULT_SIDEBAR_PROPS}
              onUserInfoClick={handleUserInfoClick}
              onRoutePlanClick={handleRoutePlanClick}
              onUserManagementClick={handleUserManagementClick}
              onDepotConfigurationClick={handleDepotConfigurationClick}
              onControlTowerClick={handleControlTowerClick}
              activeItem="controlTower"
            />
            <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.default' }}>
              <ControlTower />
            </Box>
          </Box>
        </Box>
      ) : currentView === 'userManagement' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
          <Header
            taskInfo={null}
            loading={false}
            isTaskSummary
            titleOverride="User Management"
            showHubSelector={false}
          />
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <Sidebar
              {...DEFAULT_SIDEBAR_PROPS}
              onUserInfoClick={handleUserInfoClick}
              onRoutePlanClick={handleRoutePlanClick}
              onUserManagementClick={handleUserManagementClick}
              onDepotConfigurationClick={handleDepotConfigurationClick}
              onControlTowerClick={handleControlTowerClick}
              activeItem="userManagement"
            />
            <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.default' }}>
              <UserManagementConsole />
            </Box>
          </Box>
        </Box>
      ) : (
        // Task detail view
        <>
          <Header 
            taskInfo={state.taskInfo}
            loading={state.loading}
            showMetrics={true}
            isTaskSummary={false}
            onBackToSummary={handleBackToSummary}
            headerAddon={
              <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
                <ButtonGroup size="small" variant="outlined">
                  <Button
                    variant={viewOutputMode === 'edited' ? 'contained' : 'outlined'}
                    onClick={() => handleOutputModeChange('edited')}
                  >
                    Edited
                  </Button>
                  <Button
                    variant={viewOutputMode === 'original' ? 'contained' : 'outlined'}
                    onClick={() => handleOutputModeChange('original')}
                  >
                    Original
                  </Button>
                </ButtonGroup>
                {!isOriginalMode && canCreateTrips ? (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<LocalShippingOutlinedIcon />}
                    onClick={openCreateTrips}
                  >
                    Create trips
                  </Button>
                ) : null}
                {tripsAlreadyCreated ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LocalShippingOutlinedIcon />}
                    onClick={() => navigate(`/trips/hub/${resolvedTaskDepotId || currentHubId || 'default'}`)}
                  >
                    View trips
                  </Button>
                ) : null}
              </Stack>
            }
          />
          {taskReadOnly ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Alert severity="warning">
                {tripsAlreadyCreated
                  ? 'Trips have been created from this routing task. The routing plan is now locked.'
                  : 'Original output mode is active. Trip edits and geo corrections are disabled.'}
              </Alert>
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <Sidebar 
              {...DEFAULT_SIDEBAR_PROPS}
              taskInfo={state.taskInfo} 
              loading={state.loading}
              selectedTrips={state.selectedTrips}
              onTripSelect={handleTripSelection}
              onTripVisibilityToggle={handleTripVisibility}
              searchTerm={state.searchTerm}
              onSearch={handleSearch}
              onToggleMapOption={handleMapOptions}
              showPolygons={state.showPolygons}
              showSequence={state.showSequence}
              onUserInfoClick={handleUserInfoClick}
              onRoutePlanClick={handleRoutePlanClick}
              onUserManagementClick={handleUserManagementClick}
              onDepotConfigurationClick={handleDepotConfigurationClick}
              onControlTowerClick={handleControlTowerClick}
              activeItem="routePlans"
            />
            
            {/* Main content area with trip drawer and map */}
            <Box sx={{ 
              display: 'flex', 
              flex: 1, 
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Trip drawer */}
              <TripDrawer
                taskInfo={state.taskInfo}
                selectedTrips={state.selectedTrips}
                visibleTrips={state.visibleTrips}
                searchTerm={state.searchTerm}
                onTripSelect={handleTripSelection}
                onTripVisibility={handleTripVisibility}
                onSearch={handleSearch}
                sortConfig={state.sortConfig}
                onSortKeyChange={handleTripSortKeyChange}
                onSortDirectionChange={handleTripSortDirectionChange}
                onFocusLngLat={requestMapPointFocus}
                onFocusTrip={handleTripMapFocus}
                onFocusTouchPoint={handleTripTouchPointFocus}
                onHoverTouchPoint={handleTripHover}
                editMode={state.editMode}
                editSourceTrip={state.editSourceTrip}
                selectedEditTouchPointIds={state.selectedTouchPoints.map((touchPoint) => touchPoint.tpId)}
                onToggleEditTouchPoint={taskReadOnly ? undefined : toggleEditTouchPoint}
                showTableTimelines={tableTimelinesEnabled}
                onToggleTableTimelines={handleTableTimelinesToggle}
                onOpenTimeline={handleOpenTripTimeline}
                onTimelineOrderChange={handleTableTimelineOrderChange}
                highlightedTripName={externalTripHover?.tripName ?? null}
                open={tripDrawerOpen}
                onOpenChange={setTripDrawerOpen}
                onStartTripEdit={taskReadOnly ? undefined : startTripEdit}
                onCancelTripEdit={taskReadOnly ? undefined : cancelTripEdit}
                onOcclusionRectsChange={handleTripDrawerOcclusionRectsChange}
              />
              
              {/* Map view takes remaining space and expands when drawer is collapsed */}
              <Box sx={{ 
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 0
              }}>
                <MapView 
                  taskInfo={state.taskInfo}
                  visibleTrips={state.visibleTrips}
                  showPolygons={state.showPolygons}
                  showSequence={state.showSequence}
                  defaultShowRoutes={routeGeometrySupported || !state.taskInfo?.config.sourceRouteGeometryNodeKey}
                  routeGeometryByTrip={routeGeometryByTrip}
                  routeGeometryRefreshAvailable={routeGeometrySupported && !tripsAlreadyCreated}
                  routeGeometryRefreshing={routeGeometryRefreshing}
                  onRefreshRouteGeometry={tripsAlreadyCreated ? undefined : refreshRouteGeometry}
                  editEnabled={!taskReadOnly}
                  editMode={state.editMode}
                  editSourceTrip={state.editSourceTrip}
                  editSubmitting={editSubmitting}
                  selectedTouchPoints={state.selectedTouchPoints}
                  onMapOptionsChange={handleMapOptions}
                  onStartTripEdit={taskReadOnly ? undefined : startTripEdit}
                  onTouchPointSelect={taskReadOnly ? () => undefined : toggleEditTouchPoint}
                  onCancelEdit={taskReadOnly ? () => undefined : cancelTripEdit}
                  onSubmitEdit={applyTripEdit}
                  focusLngLat={focusLngLat}
                  focusTripRequest={focusTripRequest}
                  activeTouchPointKey={activeTouchPointKey}
                  activeTripName={state.editMode ? state.editSourceTrip : null}
                  focusTouchPointRequest={focusTouchPointRequest}
                  focusOcclusionRects={focusOcclusionRects}
                  externalTouchPoint={externalTouchPointHover?.touchPoint ?? state.hoveredTouchPoint}
                  externalTouchPointAnchorClient={externalTouchPointHover?.anchorClientPosition ?? null}
                  externalTripHover={externalTripHover}
                  geoCorrectionTouchPoint={geoCorrectionTouchPoint}
                  geoCorrectionDraftLngLat={geoCorrectionDraftLngLat}
                  geoCorrectionSubmitting={geoCorrectionSubmitting}
                  onStartGeoCorrection={taskReadOnly ? undefined : startGeoCorrection}
                  onGeoCorrectionDraft={handleGeoCorrectionDraft}
                  onCancelGeoCorrection={taskReadOnly ? undefined : cancelGeoCorrection}
                  onConfirmGeoCorrection={taskReadOnly ? undefined : confirmGeoCorrection}
                  onOpenTripTimeline={handleOpenTripTimeline}
                />
              </Box>
              {openTimelineStripData.length > 0 && (
                <TimelineWorkspace
                  strips={openTimelineStripData}
                  bounds={overlayTimelineBounds}
                  position={timelineOverlayPosition}
                  width={timelineOverlayWidth ?? undefined}
                  height={timelineOverlayHeight ?? undefined}
                  mode={tableTimelinesEnabled ? 'table' : 'manual'}
                  onPositionChange={(position) => setTimelineOverlayPosition(clampTimelinePosition(position))}
                  onWidthChange={setTimelineOverlayWidth}
                  onHeightChange={setTimelineOverlayHeight}
                  onCloseTrip={handleCloseTripTimeline}
                  onCloseAll={handleCloseAllTimelines}
                  onReorderTrip={handleReorderTripTimeline}
                  onFocusTrip={handleTripMapFocus}
                  onOcclusionRectChange={handleTimelineOcclusionRectChange}
                  searchTerm={state.searchTerm}
                  sortConfig={state.sortConfig}
                  onSearch={handleSearch}
                  onSortKeyChange={handleTripSortKeyChange}
                  onSortDirectionChange={handleTripSortDirectionChange}
                  onHoverTouchPoint={handleTripHover}
                  onHoverTrip={handleTimelineTripHover}
                  onFocusTouchPoint={handleTripTouchPointFocus}
                />
              )}
            </Box>
          </Box>
          {state.error ? (
            <Box sx={{ position: 'absolute', top: 84, right: 20, zIndex: 12, maxWidth: 420 }}>
              <Alert severity="error" onClose={() => setState((prev) => ({ ...prev, error: null }))}>
                {state.error}
              </Alert>
            </Box>
          ) : null}
        </>
      )}
      <BlockingLoadingOverlay
        open={currentView === 'taskDetail' && (state.loading || editSubmitting || geoCorrectionSubmitting)}
        label={editSubmitting ? 'Applying changes' : geoCorrectionSubmitting ? 'Updating geo location' : 'Loading editor'}
        detail={
          editSubmitting
            ? 'Updating trips and locking the workspace.'
            : geoCorrectionSubmitting
              ? 'Saving the corrected location and refreshing the current workspace.'
              : 'Fetching task details and preparing the map.'
        }
      />
      <Snackbar
        open={feedback.open}
        autoHideDuration={2500}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setFeedback((prev) => ({ ...prev, open: false }))} severity={feedback.severity} sx={{ width: '100%' }}>
          {feedback.message}
        </Alert>
      </Snackbar>
      <RouteConfigModal
        open={routeConfigOpen}
        onClose={() => setRouteConfigOpen(false)}
        depotId={currentHubId || hubId || 'default'}
        tenantId={currentHub?.tenantId}
        modelType={currentHub?.modelType}
        configFilesMap={currentHub?.configFilesMap}
        onSaved={fetchHubs}
      />
      <Dialog open={createTripsOpen} onClose={() => !creatingTrips && setCreateTripsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create trips</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              This will create {state.taskInfo?.trips.length ?? 0} executable Trip records and permanently lock routing task {selectedTaskId}.
            </Typography>
            <TextField
              label="Dispatch date"
              type="date"
              value={tripDispatchDate}
              onChange={(event) => setTripDispatchDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              disabled={creatingTrips}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTripsOpen(false)} disabled={creatingTrips}>Cancel</Button>
          <Button variant="contained" onClick={submitCreateTrips} disabled={!tripDispatchDate || creatingTrips}>
            {creatingTrips ? 'Creating…' : 'Create trips'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default App;
